import { Position } from '@xyflow/react';
import {
  isAnchorAction,
  isForEachAction,
  isWorkflowEndpoint,
  SPECIAL_ACTIONS,
} from '../specialActions';
import { generateStepId, renameStepInGraph } from './stepIds';
import type { WorkflowEdge, WorkflowGraph, WorkflowNode } from './types';

/** Remove StepOutput input bindings that reference any of the deleted step ids. */
const removeStepOutputRefs = (node: WorkflowNode, deletedIds: Set<string>): WorkflowNode => {
  const input = node.data.input ?? {};
  if (Object.keys(input).length === 0) return node;
  let changed = false;
  const cleaned: Record<string, unknown> = {};
  for (const [key, binding] of Object.entries(input)) {
    if (
      binding &&
      typeof binding === 'object' &&
      'kind' in binding &&
      binding.kind === 'StepOutput' &&
      'stepId' in binding &&
      deletedIds.has(binding.stepId)
    ) {
      changed = true; // drop this binding
    } else {
      cleaned[key] = binding;
    }
  }
  if (!changed) return node;
  return { ...node, data: { ...node.data, input: cleaned as WorkflowNode['data']['input'] } };
};

/**
 * Build a fresh workflow node for an action with a unique step id. Returns
 * an array because dropping a `forEach*` action also spawns its
 * `startIteration` / `endIteration` children inside the parent's frame.
 *
 * Returns `null` when the construction is invalid — currently only the
 * "two endpoints" case (you can't drop a second `initial` or `output`).
 *
 * This is the single source of truth for node construction. Both the
 * canvas drop handler and the page-level `addNodeAtPosition` /
 * `addNodeAfter` flow through it.
 */
export const buildNewNodes = (
  actionId: string,
  position: { x: number; y: number },
  existingNodes: WorkflowNode[],
  parentStepId?: string,
): WorkflowNode[] | null => {
  if (isWorkflowEndpoint(actionId) && existingNodes.some((n) => n.data.actionId === actionId)) {
    return null;
  }

  const existingIds = new Set(existingNodes.map((n) => n.id));
  const newId = generateStepId(actionId, existingIds);
  const newNode: WorkflowNode = {
    id: newId,
    position,
    data: {
      stepId: newId,
      actionId,
      input: {},
      settings: {},
      label: actionId,
      ...(parentStepId && { parentStepId }),
    },
    type: 'workflowNode',
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    deletable: !isWorkflowEndpoint(actionId),
    ...(parentStepId && { parentId: parentStepId, extent: 'parent' as const }),
  };

  if (!isForEachAction(actionId)) {
    return [newNode];
  }

  // forEach drops auto-create iteration anchors inside the parent frame.
  newNode.style = { width: 520, height: 200 };
  existingIds.add(newId);
  const startId = generateStepId(SPECIAL_ACTIONS.startIteration, existingIds);
  existingIds.add(startId);
  const endId = generateStepId(SPECIAL_ACTIONS.endIteration, existingIds);

  const startNode: WorkflowNode = {
    id: startId,
    position: { x: 20, y: 56 },
    parentId: newId,
    extent: 'parent',
    data: {
      stepId: startId,
      actionId: SPECIAL_ACTIONS.startIteration,
      input: {},
      settings: {},
      label: SPECIAL_ACTIONS.startIteration,
      parentStepId: newId,
    },
    type: 'workflowNode',
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    deletable: false,
  };
  const endNode: WorkflowNode = {
    id: endId,
    position: { x: 280, y: 56 },
    parentId: newId,
    extent: 'parent',
    data: {
      stepId: endId,
      actionId: SPECIAL_ACTIONS.endIteration,
      input: {},
      settings: {},
      label: SPECIAL_ACTIONS.endIteration,
      parentStepId: newId,
    },
    type: 'workflowNode',
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    deletable: false,
  };
  return [newNode, startNode, endNode];
};

/** Rename one step (and update every reference to it). */
export const renameStep = (graph: WorkflowGraph, oldId: string, newId: string): WorkflowGraph => {
  if (!newId.trim() || oldId === newId) return graph;
  return renameStepInGraph(graph, oldId, newId.trim());
};

/**
 * Add one or more nodes (depending on the action) at a position on the
 * canvas. Returns the input graph unchanged when the action is invalid
 * (e.g. a second `initial`).
 */
export const addNodeAtPosition = (
  graph: WorkflowGraph,
  actionId: string,
  position: { x: number; y: number },
  parentStepId?: string,
): WorkflowGraph => {
  const newNodes = buildNewNodes(actionId, position, graph.nodes, parentStepId);
  if (!newNodes) return graph;
  return { ...graph, nodes: [...graph.nodes, ...newNodes] };
};

/**
 * Add a node connected after a source node — creates the node AND a
 * control edge from `sourceNodeId` to it. The new node inherits the
 * source's `parentStepId` (so adding after a node inside a forEach keeps
 * the new node inside the same frame).
 */
export const addNodeAfter = (
  graph: WorkflowGraph,
  sourceNodeId: string,
  actionId: string,
): WorkflowGraph => {
  const sourceNode = graph.nodes.find((n) => n.id === sourceNodeId);
  if (!sourceNode) return graph;
  if (isWorkflowEndpoint(actionId) && graph.nodes.some((n) => n.data.actionId === actionId)) {
    return graph;
  }
  const position = { x: sourceNode.position.x + 250, y: sourceNode.position.y };
  const { parentStepId } = sourceNode.data;
  const newNodes = buildNewNodes(actionId, position, graph.nodes, parentStepId);
  if (!newNodes || newNodes.length === 0) return graph;
  const newNode = newNodes[0];
  const edgeId = `control:${sourceNodeId}->${newNode.id}`;
  const newEdge: WorkflowEdge = {
    id: edgeId,
    source: sourceNodeId,
    target: newNode.id,
    sourceHandle: 'out',
    targetHandle: 'in',
    style: { strokeWidth: 1.5 },
    interactionWidth: 20,
    data: { kind: 'control', enableOnCondition: false },
  };
  return {
    nodes: [...graph.nodes, ...newNodes],
    edges: [...graph.edges, newEdge],
  };
};

/**
 * Delete one node. Cascades children when the deleted node is a forEach
 * (so we don't orphan the iteration anchors). Cleans up StepOutput input
 * bindings that referenced any of the deleted ids. Returns the input
 * graph unchanged when the target is missing or is an anchor action.
 */
export const deleteNode = (graph: WorkflowGraph, nodeId: string): WorkflowGraph => {
  const node = graph.nodes.find((n) => n.id === nodeId);
  if (!node) return graph;
  if (isAnchorAction(node.data.actionId)) return graph;
  const idsToDelete = new Set([nodeId]);
  if (isForEachAction(node.data.actionId)) {
    graph.nodes.forEach((n) => {
      if (n.data.parentStepId === nodeId) idsToDelete.add(n.id);
    });
  }

  return {
    nodes: graph.nodes
      .filter((n) => !idsToDelete.has(n.id))
      .map((n) => removeStepOutputRefs(n, idsToDelete)),
    edges: graph.edges.filter((e) => !idsToDelete.has(e.source) && !idsToDelete.has(e.target)),
  };
};

/**
 * Duplicate one node — same data, new step id, offset position.
 * Returns the input graph unchanged for endpoint actions (initial /
 * output / iteration anchors).
 */
export const duplicateNode = (graph: WorkflowGraph, nodeId: string): WorkflowGraph => {
  const sourceNode = graph.nodes.find((n) => n.id === nodeId);
  if (!sourceNode) return graph;
  if (isWorkflowEndpoint(sourceNode.data.actionId)) return graph;
  const existingIds = new Set(graph.nodes.map((n) => n.id));
  const newId = generateStepId(sourceNode.data.actionId, existingIds);
  const newNode: WorkflowNode = {
    ...sourceNode,
    id: newId,
    position: { x: sourceNode.position.x + 30, y: sourceNode.position.y + 30 },
    data: { ...sourceNode.data, stepId: newId },
    selected: false,
  };
  return { ...graph, nodes: [...graph.nodes, newNode] };
};

/**
 * Paste a previously-copied selection. Generates fresh ids and offsets
 * positions so the pasted clone doesn't sit on top of the original.
 * Endpoints (initial / output) are stripped — you can't end up with two
 * of them by pasting.
 */
export const pasteNodes = (graph: WorkflowGraph, nodesToPaste: WorkflowNode[]): WorkflowGraph => {
  if (nodesToPaste.length === 0) return graph;
  const existingIds = new Set(graph.nodes.map((n) => n.id));
  const newNodes = nodesToPaste
    .filter((n) => !isWorkflowEndpoint(n.data.actionId))
    .map((n) => {
      const newId = generateStepId(n.data.actionId, existingIds);
      existingIds.add(newId);
      return {
        ...n,
        id: newId,
        position: { x: n.position.x + 40, y: n.position.y + 40 },
        data: { ...n.data, stepId: newId },
        selected: false,
      };
    });
  if (newNodes.length === 0) return graph;
  return { ...graph, nodes: [...graph.nodes, ...newNodes] };
};

/**
 * Cut a selection: remove the nodes (and any edges touching them) and
 * scrub stale StepOutput references. Endpoints in the selection are
 * preserved by the caller before this function is called — the caller
 * is responsible for stripping un-cuttable items from `cuttableIds`.
 */
export const cutNodes = (graph: WorkflowGraph, cuttableIds: Set<string>): WorkflowGraph => {
  if (cuttableIds.size === 0) return graph;
  return {
    nodes: graph.nodes
      .filter((n) => !cuttableIds.has(n.id))
      .map((n) => removeStepOutputRefs(n, cuttableIds)),
    edges: graph.edges.filter((e) => !cuttableIds.has(e.source) && !cuttableIds.has(e.target)),
  };
};

/**
 * Add a control edge between two nodes. Returns the input graph
 * unchanged when the edge already exists.
 */
export const addControlEdge = (
  graph: WorkflowGraph,
  sourceId: string,
  targetId: string,
): WorkflowGraph => {
  const edgeId = `control:${sourceId}->${targetId}`;
  if (graph.edges.some((e) => e.id === edgeId)) return graph;
  const newEdge: WorkflowEdge = {
    id: edgeId,
    source: sourceId,
    target: targetId,
    sourceHandle: 'out',
    targetHandle: 'in',
    style: { strokeWidth: 1.5 },
    interactionWidth: 20,
    data: { kind: 'control', enableOnCondition: false },
  };
  return { ...graph, edges: [...graph.edges, newEdge] };
};

/** Remove StepOutput input bindings that reference any of the deleted step ids. */
export { removeStepOutputRefs };
